/**
 * The "Pedidos em aberto" card (platform design §5.1.1, Figma `185:5432`): only `OPEN`/
 * `IN_REVIEW` requests (`OPEN_STATES`). The full history, each answer with the team's reply,
 * opens over the page from "Ver todos os pedidos" (`RequestHistory`).
 *
 * The frame's own row instances carry a "Pedido por <nome>" line; `CapacityRequest` has no
 * requester field to read one from, so each row states when it was raised instead of who raised
 * it.
 */

import type { ReactNode } from 'react';

import { DefinitionList, type Definition } from '../../../chrome/page';
import { QueryStates } from '../../../chrome/state';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../ui/dialog';
import { getLocale, type Locale } from '../../../i18n';
import { credits as formatCredits } from '../../../i18n/format';
import { tt, type TenantMessageKey } from '../i18n';
import type { CapacityRequest } from './api/schemas';
import { useCapacityRequests } from './api/queries';
import { OPEN_STATES } from './api/queries';
import { cm, cmPlural } from './messages';

const STATE_KEY: Record<CapacityRequest['state'], TenantMessageKey> = {
  OPEN: 'request.state.OPEN',
  IN_REVIEW: 'request.state.IN_REVIEW',
  GRANTED: 'request.state.GRANTED',
  DECLINED: 'request.state.DECLINED',
  WITHDRAWN: 'request.state.WITHDRAWN',
};

function formatDate(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(iso));
}

function requestAmount(request: CapacityRequest, locale: Locale): ReactNode {
  if (request.kind === 'SLOTS') {
    return request.slots_requested === null
      ? cm('requests.noAmount')
      : cmPlural('unit.vaga.other', request.slots_requested, { n: request.slots_requested });
  }
  return request.credits_requested === null
    ? cm('requests.noAmount')
    : formatCredits(request.credits_requested, locale);
}

/** Every request, newest first as the API lists them; a decided one carries its answer date and
 *  the team's reply. */
function RequestHistory(props: { requests: CapacityRequest[]; locale: Locale }): ReactNode {
  const { requests, locale } = props;
  if (requests.length === 0) {
    return <p className="text-body-sm text-muted-foreground">{cm('requests.all.empty')}</p>;
  }
  return (
    <ul className="flex flex-col gap-4">
      {requests.map((request) => {
        const answer: Definition[] = [];
        if (request.decided_at !== null) {
          answer.push({
            term: tt('request.decided'),
            detail: formatDate(request.decided_at, locale),
          });
        }
        if (request.decision_note !== null) {
          answer.push({ term: tt('request.decision_note'), detail: request.decision_note });
        }
        return (
          <li
            key={request.id}
            className="flex flex-col gap-3 border-b pb-4 last:border-b-0 last:pb-0"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-body-sm font-medium">{requestAmount(request, locale)}</span>
                <span className="text-caption text-muted-foreground">
                  {cm('requests.createdAt', { date: formatDate(request.created_at, locale) })}
                </span>
              </div>
              <Badge variant="secondary">{tt(STATE_KEY[request.state])}</Badge>
            </div>
            {answer.length > 0 && <DefinitionList items={answer} columns={1} />}
          </li>
        );
      })}
    </ul>
  );
}

export function OpenRequests(props: { tenantId: string }): ReactNode {
  const query = useCapacityRequests(props.tenantId);
  const locale = getLocale();

  return (
    <Card>
      <CardHeader>
        <h2 className="text-title-sm">{cm('requests.heading')}</h2>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <QueryStates
          query={query}
          isEmpty={(requests) => requests.filter((r) => OPEN_STATES.has(r.state)).length === 0}
          empty={<p className="text-body-sm text-muted-foreground">{cm('requests.empty')}</p>}
        >
          {(requests) => (
            <ul className="flex flex-col gap-3">
              {requests
                .filter((request) => OPEN_STATES.has(request.state))
                .map((request) => (
                  <li
                    key={request.id}
                    className="flex items-center justify-between gap-4 border-b pb-3 last:border-b-0 last:pb-0"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-body-sm font-medium">
                        {requestAmount(request, locale)}
                      </span>
                      <span className="text-caption text-muted-foreground">
                        {cm('requests.createdAt', { date: formatDate(request.created_at, locale) })}
                      </span>
                    </div>
                    <Badge variant="secondary">{tt(STATE_KEY[request.state])}</Badge>
                  </li>
                ))}
            </ul>
          )}
        </QueryStates>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full">
              {cm('requests.viewAll')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{cm('requests.all.title')}</DialogTitle>
            </DialogHeader>
            <QueryStates query={query}>
              {(requests) => <RequestHistory requests={requests} locale={locale} />}
            </QueryStates>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
